import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Custom hook to fetch evidence name by evidenceId and casefileId
 * 
 * @param {string} evidenceId - The evidence ID
 * @param {string} casefileId - The casefile ID
 * @returns {Object} { evidenceName: string|null, loading: boolean, error: string|null }
 */
export const useEvidenceName = (evidenceId, casefileId) => {
  const [evidenceName, setEvidenceName] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Reset state when inputs change
    setEvidenceName(null);
    setError(null);
    
    // Skip if missing required parameters
    if (!evidenceId || !casefileId) {
      setLoading(false);
      return;
    }

    const fetchEvidenceName = async () => {
      setLoading(true);
      try {
        // Try nested under casefile first (primary location)
        const evidenceDoc = await getDoc(doc(db, 'casefiles', casefileId, 'evidence', evidenceId));
        
        if (evidenceDoc.exists()) {
          const evidenceData = evidenceDoc.data();
          setEvidenceName(evidenceData.fileName || evidenceData.name || null);
        } else {
          // Fallback: try top-level evidence collection
          const topLevelDoc = await getDoc(doc(db, 'evidence', evidenceId));
          if (topLevelDoc.exists()) {
            const evidenceData = topLevelDoc.data();
            setEvidenceName(evidenceData.fileName || evidenceData.name || null);
          } else {
            setEvidenceName(null);
          }
        }
      } catch (err) {
        console.error('Error fetching evidence name:', err);
        setError(err.message);
        setEvidenceName(null);
      } finally {
        setLoading(false);
      }
    };

    fetchEvidenceName();
  }, [evidenceId, casefileId]);

  return { evidenceName, loading, error };
};

/**
 * Hook for batch fetching multiple evidence names
 * Useful when displaying multiple job cards
 * 
 * @param {Array} evidenceItems - Array of {evidenceId, casefileId} objects
 * @returns {Object} { evidenceNames: Object, loading: boolean, error: string|null }
 */
export const useEvidenceNames = (evidenceItems = []) => {
  const [evidenceNames, setEvidenceNames] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Reset state
    setEvidenceNames({});
    setError(null);
    
    if (evidenceItems.length === 0) {
      setLoading(false);
      return;
    }

    const fetchEvidenceNames = async () => {
      setLoading(true);
      const names = {};
      
      try {
        // Fetch all evidence names in parallel
        const promises = evidenceItems.map(async ({ evidenceId, casefileId }) => {
          if (!evidenceId || !casefileId) return null;
          
          try {
            // Try nested under casefile first
            const evidenceDoc = await getDoc(doc(db, 'casefiles', casefileId, 'evidence', evidenceId));
            
            if (evidenceDoc.exists()) {
              const evidenceData = evidenceDoc.data();
              return {
                evidenceId,
                name: evidenceData.fileName || evidenceData.name || null
              };
            } else {
              // Fallback: try top-level evidence collection
              const topLevelDoc = await getDoc(doc(db, 'evidence', evidenceId));
              if (topLevelDoc.exists()) {
                const evidenceData = topLevelDoc.data();
                return {
                  evidenceId,
                  name: evidenceData.fileName || evidenceData.name || null
                };
              }
            }
          } catch (err) {
            console.error(`Error fetching evidence name for ${evidenceId}:`, err);
            return { evidenceId, name: null };
          }
          
          return { evidenceId, name: null };
        });

        const results = await Promise.all(promises);
        
        // Build names object
        results.forEach(result => {
          if (result) {
            names[result.evidenceId] = result.name;
          }
        });
        
        setEvidenceNames(names);
      } catch (err) {
        console.error('Error batch fetching evidence names:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchEvidenceNames();
  }, [evidenceItems]);

  return { evidenceNames, loading, error };
};
